import type { StoryItem } from '../../types/api'
import LeaderStory from '../LeaderStory/LeaderStory'
import styles from './ArticleGrouping.module.css'

interface ArticleGroupingProps {
  leader: StoryItem
  secondary: StoryItem[]
}

/**
 * Layout by group size (total = 1 leader + N secondaries):
 *
 *  N=1  (2 total): two equal-size cards side by side
 *  N=2  (3 total): leader left 60%, two secondaries stacked right
 *  N=3  (4 total): ≥900px — leader left 60%, three compact secondaries stacked right
 *                  600–899px — 2×2 grid, all equal
 *                  <600px  — all stacked vertically
 */
export default function ArticleGrouping({ leader, secondary }: ArticleGroupingProps) {
  const n = secondary.length  // 0–3

  if (n === 0) {
    return (
      <div className={leader.image_url ? styles.soloWithImage : styles.soloNoImage}>
        <LeaderStory story={leader} size="large" imageLeft noImageSolo={!leader.image_url} />
      </div>
    )
  }

  if (n === 1) {
    return (
      <div className={styles.pair}>
        <LeaderStory story={leader} size="large" imageLeft={!leader.image_url} noImageSolo={!leader.image_url} />
        <LeaderStory story={secondary[0]} size="large" imageLeft={!secondary[0].image_url} noImageSolo={!secondary[0].image_url} />
      </div>
    )
  }

  // n === 2 or n === 3: leader left, secondaries stacked right
  const secondarySize = n === 3 ? 'small' : 'medium'
  const layoutClass = n === 3 ? styles.groupFour : styles.groupThree

  return (
    <div className={`${styles.grouping} ${layoutClass}`}>
      <div className={styles.leader}>
        <LeaderStory story={leader} size="large" imageLeft={!leader.image_url} noImageSolo={!leader.image_url} />
      </div>
      <div className={styles.secondaryStack}>
        {secondary.map(story => (
          <LeaderStory 
            key={story.cluster_id} 
            story={story} 
            size={secondarySize}
            imageLeft={!story.image_url}
            noImageSolo={!story.image_url}
          />
        ))}
      </div>
    </div>
  )
}
